'use client';
import React from 'react';
import { Section, Container } from '@layout';
import {MetricCards, MetricChart, MetricDatum, Heading} from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

interface MetricCardsDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

const MetricCardsDemo = ({
  isSpanish = false,
  sectionID = 'metric-cards',
  classes = '',
  containerId,
  containerClasses,
}: MetricCardsDemoProps) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  const [groups, setGroups] = React.useState<
    { chart: MetricChart; title: string; data: MetricDatum[] }[] | null
  >(null);

  React.useEffect(() => {
    const titles = [
      t('User Interface', 'Interfaz de usuario'),
      t('User Experience', 'Experiencia de usuario'),
      t('Accessibility', 'Accesibilidad'),
    ];
    const wave = (length: number, base: number, spread: number) =>
      Array.from({ length }, () => base + Math.random() * spread);

    const line: MetricDatum[] = [
      { title: titles[0], value: 128, series: wave(24, 80, 60), baseline: 100 },
      { title: titles[1], value: 92, series: wave(24, 70, 40), baseline: 85 },
      { title: titles[2], value: 7, series: wave(24, 4, 8), baseline: 5, invert: true },
    ];
    const bar: MetricDatum[] = [
      { title: titles[0], value: 128, series: wave(8, 80, 60) },
      { title: titles[1], value: 92, series: wave(8, 70, 40) },
      { title: titles[2], value: 7, series: wave(8, 4, 8), invert: true },
    ];
    const donut: MetricDatum[] = [
      { title: titles[0], value: 86, max: 100, series: [70, 74, 78, 82, 86] },
      { title: titles[1], value: 72, max: 100, series: [80, 78, 76, 74, 72] },
      { title: titles[2], value: 64, max: 100, series: [48, 52, 55, 60, 64] },
    ];
    const area: MetricDatum[] = [
      { title: titles[0], value: 128, series: wave(24, 80, 60) },
      { title: titles[1], value: 92, series: wave(24, 70, 40) },
      { title: titles[2], value: 7, series: wave(24, 4, 8), invert: true },
    ];

    setGroups([
      { chart: 'line', title: t('Line', 'Línea'), data: line },
      { chart: 'bar', title: t('Bar', 'Barras'), data: bar },
      { chart: 'donut', title: t('Donut', 'Anillo'), data: donut },
      { chart: 'area', title: t('Area', 'Área'), data: area },
    ]);
  }, [isSpanish]);

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as="h3">{t('Metric Cards', 'Tarjetas de métricas')}</Heading>
        <p className='sub full'>
          {t(
            'Small metric cards for dashboards and summaries. The same card draws a line, bar, donut, or area chart.',
            'Tarjetas pequeñas de métricas para paneles y resúmenes. La misma tarjeta dibuja un gráfico de línea, barras, anillo o área.'
          )}
        </p>
        {groups ? (
          groups.map((group) => (
            <div key={group.chart} className='metric-chart-example'>
              <Heading as='h4'>{group.title}</Heading>
              <MetricCards chart={group.chart} data={group.data} />
            </div>
          ))
        ) : (
          <MetricCards />
        )}
        <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<article class="card metric">
  <div class="metric-top">
    <span class="metric-title">Active</span>
    <span class="change up">+4%</span>
  </div>
  <div class="metric-value">128</div>
</article>`}
        />
      </Container>
    </Section>
  );
};

export default MetricCardsDemo;
